import { DuplicateApnDialog, duplicateFromError } from "../components/DuplicateApnDialog";
import { usePersistedState } from "../lib/usePersistedState";
import { formatRecordId } from "../lib/utils-format";
import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Search, Table2, LayoutGrid, X, AlertTriangle, Archive, Flame, Upload, Loader2, PhoneIncoming, QrCode } from "lucide-react";
import PageHeader from "../components/PageHeader";
import DataTable from "../components/DataTable";
import EntityForm from "../components/EntityForm";
import KanbanBoard from "../components/KanbanBoard";
import { StatusBadge } from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useEntity, createItem, updateItem } from "../hooks/useEntity";
import { useAuth } from "../context/AuthContext";
import api, { apiError } from "../lib/api";
import { LEAD_STATUSES, STATUS_PATHS, CREATE_PROJECT_FIELDS, CREATE_PROPERTY_SEARCH_FIELDS, CREATE_INBOUND_LEAD_FIELDS, isLeadOnMarket } from "../config/recordConfigs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";

const VERDICT_PILL = {
  "GO": "bg-emerald-500/15 text-emerald-500",
  "CAUTION": "bg-amber-500/15 text-amber-600",
  "NO-GO": "bg-red-500/15 text-red-500",
};
const VERDICT_COLUMN = {
  key: "angela_verdict", label: "Verdict",
  render: (r) => r.angela_verdict
    ? <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${VERDICT_PILL[r.angela_verdict] || "bg-secondary text-muted-foreground"}`} data-testid={`verdict-badge-${r.id}`}>{r.angela_verdict}</span>
    : <span className="text-muted-foreground">—</span>,
};

const RATING_HEX = { green: "#22c55e", yellow: "#eab308", red: "#ef4444" };
const RATING_COLUMN = {
  key: "rating_score", label: "Rating",
  render: (r) => r.rating_score != null
    ? <span className="inline-flex items-center gap-1.5" data-testid={`rating-badge-${r.id}`}>
        <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: RATING_HEX[r.rating_color] || RATING_HEX.yellow, boxShadow: `0 0 8px ${RATING_HEX[r.rating_color] || RATING_HEX.yellow}66` }} />
        <span className="text-sm font-medium">{r.rating_score}/10</span>
      </span>
    : <span className="text-muted-foreground">—</span>,
};

const LEAD_ID_COLUMN = {
  key: "record_no", label: "Lead ID",
  render: (r) => <span className="text-sm font-medium whitespace-nowrap" data-testid={`lead-id-${r.id}`}>{r.record_no ? formatRecordId("inbound_leads", r.record_no) : "—"}</span>,
};

const MARKET_COLUMN = {
  key: "market", label: "Market",
  render: (r) => {
    const on = isLeadOnMarket(r);
    return (
      <span data-testid={`market-badge-${r.id}`}
        className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap ${on ? "bg-emerald-500/15 text-emerald-500" : "bg-sky-500/15 text-sky-400"}`}>
        {on ? "On-Market" : "Off-Market"}
      </span>
    );
  },
};

const COLUMNS = [
  { key: "lead_status", label: "Status", badge: true },
  { key: "first_name", label: "Project Name", render: (r) => r.property_name || r.property_analysis_name || r.project_name || `${r.first_name || ""} ${r.last_name || ""}`.trim() || r.street_address || r.address || "—" },
  { key: "address", label: "Address", render: (r) => [r.property_no, r.street_name].filter(Boolean).join(" ") || r.address || "—" },
  { key: "city", label: "City", render: (r) => r.city || "—" },
  { key: "state", label: "State", render: (r) => r.state || "—" },
  { key: "property_classification", label: "Property Type", render: (r) => r.property_classification || "—" },
  { key: "owner_name", label: "Owner", render: (r) => r.owner_name || "Unassigned" },
];

export default function Leads({ title = "Project", subtitle = "Capture, qualify, and route inbound demand", resource: resourceProp = "leads", showOwnerFilter = true }) {
  // "analysis" is the canonical name for the Property Analysis module; the physical collection
  // is "leads" (legacy). Normalize here so callers can pass either.
  const resource = resourceProp === "analysis" ? "leads" : resourceProp;
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const sourcedJobId = searchParams.get("sourced_job_id");
  const sourcedJobName = searchParams.get("sourced_job_name");
  const [view, setView] = useState("table");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = usePersistedState(`filters:${resourceProp}:status`, "all");
  const [marketFilter, setMarketFilter] = usePersistedState(`filters:${resourceProp}:market`, "all");
  const [sourceFilter, setSourceFilter] = usePersistedState(`filters:${resourceProp}:source`, "all");
  const [propTypeFilter, setPropTypeFilter] = usePersistedState(`filters:${resourceProp}:propType`, "all");
  const [ownerFilter, setOwnerFilter] = usePersistedState(`filters:${resourceProp}:ownerScope`, "mine");
  const [ownerId, setOwnerId] = usePersistedState(`filters:${resourceProp}:owner`, "all");
  const [owners, setOwners] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const importInputRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const handleImportFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (f) await importFile(f);
  };
  const importFile = async (f, allowDuplicate = false) => {
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/inbound_leads/import", fd, {
        headers: { "Content-Type": "multipart/form-data" }, params: { allow_duplicate: allowDuplicate } });
      setDupModal(null);
      toast.success("Lead imported from " + f.name);
      navigate(`/record/inbound_leads/${data.id}`);
    } catch (err) {
      const d = duplicateFromError(err);
      if (d) { setDupModal({ ...d, importFile: f }); return; }
      toast.error(apiError(err));
    } finally {
      setImporting(false);
    }
  };
  const [campaigns, setCampaigns] = useState([]);
  const [dupModal, setDupModal] = useState(null); // { matches, form } for potential-duplicate APN warning

  const params = { search, limit: 200 };
  if (statusFilter !== "all") params.lead_status = statusFilter;
  if (resource === "inbound_leads" && sourceFilter !== "all") {
    params.lead_source = sourceFilter === "melissa" ? "Retell Inbound Call" : "Sell Your Land Portal";
  }
  // Sourced-search view shows every resulting lead (incl. unassigned), so skip the owner filter.
  if (sourcedJobId) {
    params.sourced_job_id = sourcedJobId;
  } else if (!showOwnerFilter) {
    // Owner filter hidden for this view — show all records regardless of owner.
  } else if (isAdmin) {
    // Admins see all leads and filter by a specific owner via the dropdown.
    if (ownerId !== "all") params.owner_id = ownerId;
  } else if (ownerFilter === "mine" && user?.id) {
    params.owner_id = user.id;
  }
  const { items, total, loading, reload } = useEntity(resource, params);

  // Rank the Property Analysis list: best deal first (Angela's verdict, then profit margin).
  // Multi-lot deals rank on their blended (combined) margin & profit.
  const rankedItems = resource === "leads" ? [...items].sort((a, b) => {
    const rank = (r) => ({ "GO": 3, "CAUTION": 2, "NO-GO": 1 }[r.angela_verdict] || 0);
    const eff = (r) => {
      const s = r.subdivision;
      if (s && s.enabled && (s.lots || []).length > 0) {
        const arv = (s.lots || []).reduce((t, l) => t + (Number(l.arv) || 0), 0);
        const allIn = (s.lots || []).reduce((t, l) => t + (Number(l.cost) || 0), 0)
          + (s.site_costs || []).reduce((t, x) => t + (Number(x.amount) || 0), 0);
        return { arv, profit: arv - allIn };
      }
      return { arv: Number(r.arv_market_value) || 0, profit: Number(r.est_net_profit) || 0 };
    };
    const margin = (r) => { const e = eff(r); return e.arv > 0 ? e.profit / e.arv : -Infinity; };
    return rank(b) - rank(a) || margin(b) - margin(a) || eff(b).profit - eff(a).profit;
  }) : resource === "inbound_leads" ? [...items].sort((a, b) => ((b.is_hot ? 1 : 0) - (a.is_hot ? 1 : 0)) || (new Date(b.created_at || 0) - new Date(a.created_at || 0))) : items;

  // On/Off-market filter (inbound leads only) + Property Type filter (all list views).
  const applyMarket = (list) => {
    let out = (resource === "inbound_leads" && marketFilter !== "all")
      ? list.filter((r) => (marketFilter === "on" ? isLeadOnMarket(r) : !isLeadOnMarket(r)))
      : list;
    if (propTypeFilter !== "all") out = out.filter((r) => (r.property_classification || "") === propTypeFilter);
    return out;
  };

  // Angela's ranking: best deal = #1 (drives what to focus on first). Position in the sorted list.
  const rankMap = {};
  rankedItems.forEach((r, i) => { rankMap[r.id] = i + 1; });
  const RANK_COLUMN = {
    key: "ranked", label: "Ranked",
    render: (r) => {
      const n = rankMap[r.id];
      if (!n) return <span className="text-muted-foreground">—</span>;
      return (
        <span
          data-testid={`rank-badge-${r.id}`}
          className="inline-flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold bg-secondary text-foreground"
        >
          {n}
        </span>
      );
    },
  };

  const clearSourcedFilter = () => {
    searchParams.delete("sourced_job_id");
    searchParams.delete("sourced_job_name");
    setSearchParams(searchParams);
  };

  useEffect(() => {
    api.get("/campaigns?limit=200").then((r) => setCampaigns(r.data.items || [])).catch(() => {});
    if (isAdmin) api.get("/lead-owners").then((r) => setOwners(r.data || [])).catch(() => {});
  }, [isAdmin]);

  const campaignOptions = campaigns.map((c) => ({ value: c.id, label: c.name || c.campaign_name }));

  const submit = (form) => doCreate(form);

  const doCreate = async (form, allowDuplicate = false) => {
    setSaving(true);
    try {
      const payload = { ...form, status: form.lead_status, allow_duplicate: allowDuplicate };
      // New top-of-funnel leads default to the "New" stage.
      if (resource === "inbound_leads" && !payload.lead_status) { payload.lead_status = "New"; payload.status = "New"; }
      // Manual leads: translate the Deal Channel choice into the lead's market channel.
      if (resource === "inbound_leads") {
        const mt = payload.market_type;
        delete payload.market_type;
        payload.source_type = mt === "On-Market" ? "ACTIVE" : "OFF-MARKET";
      }
      if (form.campaign_id) {
        const c = campaigns.find((x) => x.id === form.campaign_id);
        payload.campaign_name = c ? (c.name || c.campaign_name) : undefined;
      }
      await createItem(resource, payload);
      toast.success(`${title} created`);
      setDupModal(null); setFormOpen(false); reload();
    } catch (e) {
      const d = duplicateFromError(e);
      if (d) setDupModal({ ...d, form });
      else toast.error(apiError(e));
    } finally { setSaving(false); }
  };

  const move = async (id, stage) => {
    await updateItem(resource, id, { lead_status: stage });
    toast.success(`Moved to ${stage}`); reload();
  };

  const open = (r) => navigate(`/record/${resource}/${r.id}`);

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1400px] mx-auto">
      <PageHeader
        title={title} count={total} subtitle={subtitle}
        actions={
          <div className="flex flex-wrap items-center justify-end gap-2">
            <div className="flex rounded-md border border-border overflow-hidden">
              <button onClick={() => setView("table")} className={`px-3 py-2 ${view === "table" ? "bg-primary text-primary-foreground" : "hover:bg-accent"}`} data-testid="view-table-btn"><Table2 className="h-4 w-4" /></button>
              <button onClick={() => setView("kanban")} className={`px-3 py-2 ${view === "kanban" ? "bg-primary text-primary-foreground" : "hover:bg-accent"}`} data-testid="view-kanban-btn"><LayoutGrid className="h-4 w-4" /></button>
            </div>
            {resource === "inbound_leads" && (
              <>
                <input ref={importInputRef} type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={handleImportFile} data-testid="import-lead-input" />
                <Button variant="outline" onClick={() => importInputRef.current?.click()} disabled={importing} data-testid="import-lead-btn">
                  {importing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Upload className="h-4 w-4 mr-1.5" />}Import Lead
                </Button>
              </>
            )}
            <Button onClick={() => setFormOpen(true)} data-testid="new-record-btn"><Plus className="h-4 w-4 mr-1.5" />{resource === "property_search" ? "New Property" : resource === "inbound_leads" ? "New Lead" : "New Project"}</Button>
          </div>
        }
      />

      {sourcedJobId && (
        <div className="flex items-center gap-3 mb-4 rounded-md border border-primary/40 bg-primary/10 px-4 py-2.5 text-sm" data-testid="sourced-filter-banner">
          <span className="text-primary font-medium">
            Showing {total} lead{total === 1 ? "" : "s"} imported from search{sourcedJobName ? `: ${sourcedJobName}` : ""}
          </span>
          <button onClick={clearSourcedFilter} className="ml-auto inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground" data-testid="sourced-filter-clear">
            <X className="h-3.5 w-3.5" /> Clear
          </button>
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="search-input" />
        </div>
        {showOwnerFilter && !sourcedJobId && isAdmin && (
          <Select value={ownerId} onValueChange={setOwnerId}>
            <SelectTrigger className="w-full sm:w-[200px]" data-testid="owner-filter-select"><SelectValue placeholder="All owners" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All owners</SelectItem>
              {owners.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        {showOwnerFilter && !sourcedJobId && !isAdmin && (
          <div className="flex rounded-md border border-border overflow-hidden shrink-0" data-testid="owner-filter">
            <button onClick={() => setOwnerFilter("mine")} className={`px-4 py-2 text-sm font-medium ${ownerFilter === "mine" ? "bg-primary text-primary-foreground" : "hover:bg-accent"}`} data-testid="owner-filter-mine">My Leads</button>
            <button onClick={() => setOwnerFilter("all")} className={`px-4 py-2 text-sm font-medium ${ownerFilter === "all" ? "bg-primary text-primary-foreground" : "hover:bg-accent"}`} data-testid="owner-filter-all">All Leads</button>
          </div>
        )}
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-[200px]" data-testid="status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {(STATUS_PATHS[resource] || LEAD_STATUSES).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={propTypeFilter} onValueChange={setPropTypeFilter}>
          <SelectTrigger className="w-full sm:w-[180px]" data-testid="property-type-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Property Types</SelectItem>
            <SelectItem value="Residential">Residential</SelectItem>
            <SelectItem value="Commercial">Commercial</SelectItem>
          </SelectContent>
        </Select>
        {resource === "inbound_leads" && (
          <Select value={marketFilter} onValueChange={setMarketFilter}>
            <SelectTrigger className="w-full sm:w-[180px]" data-testid="market-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Markets</SelectItem>
              <SelectItem value="on">On-Market</SelectItem>
              <SelectItem value="off">Off-Market</SelectItem>
            </SelectContent>
          </Select>
        )}
        {resource === "inbound_leads" && (
          <Select value={sourceFilter} onValueChange={setSourceFilter}>
            <SelectTrigger className="w-full sm:w-[170px]" data-testid="source-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Sources</SelectItem>
              <SelectItem value="melissa">Melissa (Inbound Call)</SelectItem>
              <SelectItem value="qr">QR / Sell Your Land</SelectItem>
            </SelectContent>
          </Select>
        )}
      </div>

      {view === "table" ? (
        <DataTable columns={resource === "leads" ? [RANK_COLUMN, ...COLUMNS.filter((c) => c.key !== "address"), VERDICT_COLUMN] : resource === "property_search" ? [...COLUMNS.filter((c) => c.key !== "address").map((c) => c.key === "lead_status" ? { ...c, badge: false, render: (r) => <StatusBadge value={r.lead_status || "New"} /> } : c), RATING_COLUMN] : resource === "inbound_leads" ? [LEAD_ID_COLUMN, ...COLUMNS.filter((c) => c.key !== "address" && c.key !== "owner_name").map((c) => {
          if (c.key === "lead_status") return { ...c, badge: false, render: (r) => <StatusBadge value={r.lead_status || "New"} /> };
          if (c.key === "first_name") return { ...c, render: (r) => (
            <span className="inline-flex items-center gap-2">
              {r.is_hot && (
                <span title="Hot lead" data-testid={`hot-flame-${r.id}`} className="inline-flex items-center rounded-full bg-red-500/15 px-1 py-0.5 text-red-500">
                  <Flame className="h-3.5 w-3.5 shrink-0" style={{ filter: "drop-shadow(0 0 4px rgba(239,68,68,0.8))" }} />
                </span>
              )}
              <span>{c.render(r)}</span>
              {r.lead_source === "Angela Discovery" && (r.lead_status || "New") === "New" && (
                <span title="Fresh find by Angela" data-testid={`angela-fire-${r.id}`} className="inline-flex items-center gap-1 rounded-full bg-orange-500/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-orange-500">
                  <Flame className="h-3 w-3 shrink-0" style={{ filter: "drop-shadow(0 0 4px rgba(249,115,22,0.7))" }} /> New
                </span>
              )}
              {r.lead_source === "Retell Inbound Call" && (
                <span title="Inbound call captured by Melissa (AI voice agent)" data-testid={`melissa-badge-${r.id}`} className="inline-flex items-center gap-1 rounded-full bg-violet-500/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-violet-400">
                  <PhoneIncoming className="h-3 w-3 shrink-0" /> Melissa
                </span>
              )}
              {r.lead_source === "Sell Your Land Portal" && (
                <span title="Submitted via QR / Sell Your Land form" data-testid={`qr-badge-${r.id}`} className="inline-flex items-center gap-1 rounded-full bg-sky-500/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-sky-400">
                  <QrCode className="h-3 w-3 shrink-0" /> QR
                </span>
              )}
            </span>
          ) };
          return c;
        }), MARKET_COLUMN] : resource === "projects" ? COLUMNS.filter((c) => c.key !== "address") : COLUMNS} items={applyMarket(rankedItems)} loading={loading} onRowClick={open} emptyLabel="No records found" />
      ) : (
        <KanbanBoard
          columns={STATUS_PATHS[resource] || LEAD_STATUSES} items={applyMarket(items)} groupKey="lead_status" onMove={move}
          renderCard={(it) => (
            <div onClick={() => open(it)}>
              <div className="font-medium text-sm">{it.first_name} {it.last_name}</div>
              <div className="text-xs text-muted-foreground">{it.company_name}</div>
              <div className="flex items-center justify-between mt-2">
                <StatusBadge value={it.temperature} />
                <span className="text-xs text-muted-foreground">Score {it.lead_score}</span>
              </div>
            </div>
          )}
        />
      )}

      <EntityForm open={formOpen} onOpenChange={setFormOpen} fields={resource === "property_search" ? CREATE_PROPERTY_SEARCH_FIELDS : resource === "inbound_leads" ? CREATE_INBOUND_LEAD_FIELDS : CREATE_PROJECT_FIELDS} initial={null} onSubmit={submit} saving={saving} title={resource === "property_search" ? "New Property" : resource === "inbound_leads" ? "New Lead" : "New Project"} />

      <DuplicateApnDialog dup={dupModal} busy={saving} onClose={() => setDupModal(null)}
        onCreateAnyway={() => (dupModal.importFile ? importFile(dupModal.importFile, true) : doCreate(dupModal.form, true))} />
    </div>
  );
}
